import { useEffect, useMemo, useRef, useState } from 'react';

import { useGetContainerTypesQuery, useGetExistContainerQuery } from '@api/container';
import { ContainerContainerByTypeListParams, ContainerIncludeOption, Workflow } from '@rdTypes/apiContract';

import { getDateISOString } from '@utils/time';

import { StorageGroupProps } from './types';
import { useDefaultContainerValue } from './useDefaultContainerValue';
import { SortingOrder } from 'src/types/contractHelpers';

export const useStorageGroup = ({
  piece,
  workflow,
  onChange,
  onLoading,
  isLoading,
  isNextOrderNo,
  onContainerFound,
}: StorageGroupProps) => {
  const { data: containerTypes = [] } = useGetContainerTypesQuery();
  const defaultValue = useDefaultContainerValue(workflow, piece?.pieceLocation?.container);
  const [isDebouncing, setIsDebouncing] = useState(false);
  const [container, setContainer] = useState({
    id: null,
    date:
      defaultValue?.createdDate || workflow === Workflow.MailBox
        ? new Date(defaultValue?.createdDate || Date.now())
        : null,
    containerTypeId: defaultValue.containerTypeId,
    containerNumber: defaultValue.containerNumber || '',
    sortingOrder: defaultValue.sortOrder,
  });
  const { date, containerTypeId, containerNumber, sortingOrder } = container;
  const prevWorkflow = useRef<Workflow | null>(null);

  const existingContainerRequestData = useMemo<ContainerContainerByTypeListParams>(() => {
    const requestData: ContainerContainerByTypeListParams = {};
    if (date) requestData['Filter.CreatedDate'] = getDateISOString(date);
    if (containerTypeId) requestData['Filter.ContainerTypeId'] = +containerTypeId;
    requestData['Filter.ContainerNumber'] = containerNumber || '';

    return requestData;
  }, [date, containerTypeId, containerNumber]);

  const isSkip = !existingContainerRequestData['Filter.ContainerTypeId'] ||
        [Workflow.HighDensityMail, Workflow.MailBox].includes(workflow);

  const { data: existingContainerSearch, isFetching: fetchingExistingContainer } = useGetExistContainerQuery(
    {
      ...existingContainerRequestData,
      workflow: workflow,
      Include: [ContainerIncludeOption.Summary],
    },
    {
      skip: isSkip,
      refetchOnMountOrArgChange: true,
    },
  );

  const onChangeValue = (key: string) => (value: any) => {
    const nextContainer = { ...container, [key]: value };
    setContainer(nextContainer);
    onChange({
      id: nextContainer.id,
      createdDate: nextContainer.date ? getDateISOString(nextContainer.date) : null,
      containerTypeId: nextContainer.containerTypeId,
      containerNumber: nextContainer.containerNumber,
      sortOrder: nextContainer.sortingOrder,
    });
  };

  if (((isLoading !== (fetchingExistingContainer || isDebouncing)) || (isSkip && isLoading)) && onLoading) {
    onLoading(isSkip ? false : fetchingExistingContainer || isDebouncing);
  }

  if (container.id !== (existingContainerSearch?.id || null)) {
    onChangeValue('id')(existingContainerSearch?.id || null);
  }

  if (prevWorkflow.current && prevWorkflow.current !== workflow) {
    const nextContainer = {
      id: null,
      date: workflow === Workflow.MailBox ? new Date() : null,
      containerTypeId: containerTypes?.find((type) => type.workflow === workflow)?.id || 0,
      containerNumber: workflow === Workflow.Carrier ? 'USPS' : '',
      sortingOrder: defaultValue.sortOrder,
    };
    setContainer(nextContainer);
    onChange({
      ...nextContainer,
      containerTypeId: +nextContainer.containerTypeId,
    });
  }

  prevWorkflow.current = workflow;

  useEffect(() => {
    const nextSortOrder = (existingContainerSearch?.sortOrder as SortingOrder) || defaultValue.sortOrder;
    if (nextSortOrder !== sortingOrder) {
      setContainer({
        ...container,
        sortingOrder: nextSortOrder,
      });
    }

    if (isNextOrderNo) {
      onChange({
        id: container.id,
        createdDate: container.date ? getDateISOString(container.date) : null,
        containerTypeId: +container.containerTypeId,
        containerNumber: container.containerNumber,
        sortOrder: nextSortOrder !== sortingOrder ? nextSortOrder : container.sortingOrder,
        nextOrderNo: existingContainerSearch?.nextOrderNo,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [existingContainerSearch]);

  useEffect(() => {
    onContainerFound?.(existingContainerSearch);
  }, [existingContainerSearch, onContainerFound]);

  return {
    containerTypeId,
    date,
    setIsDebouncing,
    containerNumber,
    existingContainer: existingContainerSearch,
    sortingOrder,
    fetchingExistingContainer,
    onChangeValue,
  };
};
